/**
 * SoftStopQueue — /#/manager/soft-stop-queue
 *
 * RULE-ESC-1 manager queue. Shows every PENDING_REVIEW SOFT_STOP event for the
 * current tenant, the elapsed time since it was opened, the 72-hour deadline,
 * and a per-lane decision form (no blanket button).
 *
 * Also shows the full audit log below the queue. Manager-only.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api } from "@/lib/session";

interface QueueEvent {
  id: string;
  deal_id: number;
  tenant_id: number;
  lane_key: string;
  cp_number: number;
  question_key: string;
  triggering_option_key: string;
  created_at: string;
  escalation_deadline: string;
  status: string;
  assigned_manager_id: string;
  resolved_at: string | null;
  decision: string | null;
  decision_reason: string | null;
  escalation_fired: boolean;
  escalation_fired_at: string | null;
  escalation_contact_email: string | null;
  prior_soft_stop_context: any;
}

interface AuditEntry {
  id: string;
  soft_stop_event_id: string;
  deal_id: number;
  lane_key: string;
  cp_number: number;
  question_key: string;
  event_type: string;
  timestamp: string;
  actor_id: string;
  actor_name: string;
  decision: string | null;
  reason: string | null;
}

function elapsed(start: string, end: string | null = null): string {
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const ms = Math.max(0, b - a);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

function statusColor(s: string): string {
  if (s === "ESCALATED") return "#DC2626";
  if (s === "PENDING_REVIEW") return "#CA8A04";
  if (s === "RESOLVED_POST_ESC") return "#7C3AED";
  return "#16A34A";
}

export default function SoftStopQueue() {
  const [events, setEvents] = useState<QueueEvent[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"PENDING_REVIEW" | "ESCALATED" | "ALL">(
    "PENDING_REVIEW",
  );

  const [openId, setOpenId] = useState<string | null>(null);
  const [decision, setDecision] = useState<string>("APPROVE");
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [q, a] = await Promise.all([
        api(`/api/soft-stop/queue?status=${filter}&limit=200`),
        api("/api/soft-stop/audit?limit=200"),
      ]);
      setEvents(q.events ?? []);
      setAudit(a.entries ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [filter]);

  async function submitDecision(eventId: string) {
    if (!reason.trim()) {
      alert("Reason is required. No canned options permitted.");
      return;
    }
    setSubmitting(true);
    try {
      await api(`/api/soft-stop/events/${eventId}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision, reason }),
      });
      setOpenId(null);
      setReason("");
      setDecision("APPROVE");
      await refresh();
    } catch (e: any) {
      alert(`Decision failed: ${e?.message ?? "error"}`);
    } finally {
      setSubmitting(false);
    }
  }

  const grouped = useMemo(() => {
    const m = new Map<number, QueueEvent[]>();
    for (const e of events) {
      const arr = m.get(e.deal_id) ?? [];
      arr.push(e);
      m.set(e.deal_id, arr);
    }
    return [...m.entries()];
  }, [events]);

  return (
    <OperatorShell breadcrumbs={[{ label: "Soft Stop Queue" }]} pageTitle="Soft Stop Queue" pageMeta="Manager escalation queue">
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
        SOFT_STOP Manager Queue
      </h1>
      <p style={{ color: "#64748B", marginBottom: 24, fontSize: 14 }}>
        Per-lane decisions. Each row is one SOFT_STOP condition on one deal.
        72-hour window. After the deadline, the deal escalates to the tenant
        escalation contact.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["PENDING_REVIEW", "ESCALATED", "ALL"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid #E2E8F0",
              background: filter === f ? "#0B0B0F" : "white",
              color: filter === f ? "white" : "#0B0B0F",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {f.replace("_", " ")}
          </button>
        ))}
        <button
          onClick={() => void refresh()}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            border: "1px solid #E2E8F0",
            background: "white",
            cursor: "pointer",
            fontSize: 13,
            marginLeft: "auto",
          }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            background: "#FEE2E2",
            color: "#991B1B",
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: "#64748B" }}>Loading queue...</div>
      ) : events.length === 0 ? (
        <div style={{ color: "#64748B" }}>No events match this filter.</div>
      ) : (
        <div style={{ marginBottom: 32 }}>
          {grouped.map(([dealId, lanes]) => (
            <div
              key={dealId}
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                marginBottom: 16,
                background: "white",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #E2E8F0",
                  background: "#F8FAFC",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Deal #{dealId}
                {lanes[0]?.prior_soft_stop_context?.dealName
                  ? ` — ${lanes[0].prior_soft_stop_context.dealName}`
                  : ""}
                <span style={{ marginLeft: 12, color: "#64748B", fontWeight: 400 }}>
                  {lanes.length} lane{lanes.length === 1 ? "" : "s"}
                </span>
              </div>
              {lanes.map((e) => (
                <div
                  key={e.id}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #F1F5F9",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        color: statusColor(e.status),
                      }}
                    >
                      {e.status.replace(/_/g, " ")}
                    </span>
                    <strong style={{ fontSize: 14 }}>{e.lane_key}</strong>
                    <span style={{ fontSize: 12, color: "#64748B" }}>
                      CP{e.cp_number} · {e.question_key}
                    </span>
                    <span style={{ fontSize: 12, color: "#64748B", marginLeft: "auto" }}>
                      Opened {elapsed(e.created_at)} ago · Deadline{" "}
                      {new Date(e.escalation_deadline).toLocaleString()}
                    </span>
                  </div>
                  {e.escalation_fired && (
                    <div style={{ fontSize: 12, color: "#DC2626" }}>
                      Escalated to {e.escalation_contact_email} at{" "}
                      {e.escalation_fired_at}
                    </div>
                  )}
                  {e.status === "PENDING_REVIEW" || e.status === "ESCALATED" ? (
                    openId === e.id ? (
                      <div
                        style={{
                          marginTop: 8,
                          padding: 12,
                          background: "#F8FAFC",
                          borderRadius: 6,
                        }}
                      >
                        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                          {["APPROVE", "RETURN", "ADEQUATE", "INADEQUATE"].map((d) => (
                            <label key={d} style={{ fontSize: 13 }}>
                              <input
                                type="radio"
                                name={`d-${e.id}`}
                                checked={decision === d}
                                onChange={() => setDecision(d)}
                              />{" "}
                              {d}
                            </label>
                          ))}
                        </div>
                        <textarea
                          value={reason}
                          onChange={(ev) => setReason(ev.target.value)}
                          placeholder="Reason (required — free text, no canned options)"
                          style={{
                            width: "100%",
                            minHeight: 60,
                            padding: 8,
                            border: "1px solid #E2E8F0",
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                        />
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button
                            onClick={() => void submitDecision(e.id)}
                            disabled={submitting || !reason.trim()}
                            style={{
                              padding: "6px 14px",
                              background: "#0B0B0F",
                              color: "white",
                              border: "none",
                              borderRadius: 6,
                              cursor: submitting ? "wait" : "pointer",
                              fontSize: 13,
                              opacity: !reason.trim() ? 0.5 : 1,
                            }}
                          >
                            {submitting ? "Submitting..." : "Submit decision"}
                          </button>
                          <button
                            onClick={() => {
                              setOpenId(null);
                              setReason("");
                            }}
                            style={{
                              padding: "6px 14px",
                              background: "white",
                              border: "1px solid #E2E8F0",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 13,
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setOpenId(e.id);
                          setReason("");
                          setDecision("APPROVE");
                        }}
                        style={{
                          alignSelf: "flex-start",
                          padding: "4px 12px",
                          background: "white",
                          border: "1px solid #0B0B0F",
                          color: "#0B0B0F",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                      >
                        Record decision
                      </button>
                    )
                  ) : (
                    <div style={{ fontSize: 12, color: "#16A34A" }}>
                      {e.decision} — {e.decision_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 32, marginBottom: 12 }}>
        Audit log
      </h2>
      <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#F8FAFC" }}>
            <tr>
              <th style={{ textAlign: "left", padding: 10 }}>When</th>
              <th style={{ textAlign: "left", padding: 10 }}>Type</th>
              <th style={{ textAlign: "left", padding: 10 }}>Deal</th>
              <th style={{ textAlign: "left", padding: 10 }}>Lane</th>
              <th style={{ textAlign: "left", padding: 10 }}>Actor</th>
              <th style={{ textAlign: "left", padding: 10 }}>Decision</th>
              <th style={{ textAlign: "left", padding: 10 }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {audit.map((a) => (
              <tr key={a.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: 10, whiteSpace: "nowrap" }}>
                  {new Date(a.timestamp).toLocaleString()}
                </td>
                <td style={{ padding: 10 }}>{a.event_type}</td>
                <td style={{ padding: 10 }}>#{a.deal_id}</td>
                <td style={{ padding: 10 }}>
                  {a.lane_key}
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    CP{a.cp_number} {a.question_key}
                  </div>
                </td>
                <td style={{ padding: 10 }}>{a.actor_name}</td>
                <td style={{ padding: 10 }}>{a.decision ?? ""}</td>
                <td style={{ padding: 10, maxWidth: 320, whiteSpace: "normal" }}>
                  {a.reason ?? ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </OperatorShell>
  );
}
